import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { pollCreativePendingCount } from 'modules/api/creativeNotifications';
import { Store } from 'components/common/types/Store.types';
import { showBrowserNotification } from 'lib/BrowserNotification/BrowserNotification';
import { updatePendingCreativesCount } from 'store/pages/contentManagement/creatives/reducer';
import { CreativeStatus } from 'components/common/types/Creative.types';
import { cancelFunctions } from 'utils/cancelFunctions';

export const useCreativeNotifications = (): void => {
  const tenMinutesInMilliseconds = 600000;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const environmentId = useSelector((state: Store) => state.environment.environmentId);
  const currentCreativesCountRef = useRef(0);

  const tryShowCreativeCountNotification = async (newCreativesCount: number): Promise<void> => {
    const creativeCountDiff = newCreativesCount - currentCreativesCountRef.current;

    if (
      creativeCountDiff < 0 ||
      (creativeCountDiff > 0 &&
        (await showBrowserNotification({
          title: 'VIOOH - Creative notification',
          body: `${creativeCountDiff} new creative${creativeCountDiff > 1 ? 's' : ''} to approve`,
          onClick: () => {
            pendo?.track('CHROME_CREATIVE_NOTIFICATION');
            navigate(`/content-management/creatives?status=${CreativeStatus.PENDING}`);
          },
        })))
    ) {
      currentCreativesCountRef.current = newCreativesCount;
    }
  };

  const pollPendingCreativesCount = (): void => {
    const poll = pollCreativePendingCount(environmentId, cancelFunctions);

    poll(({ data }: { data: number }) => {
      dispatch(updatePendingCreativesCount(data));
      tryShowCreativeCountNotification(data);
    }, tenMinutesInMilliseconds).catch(() => {});
  };

  useEffect(() => {
    if (environmentId) {
      currentCreativesCountRef.current = 0;
      pollPendingCreativesCount();
    }

    if (!environmentId) {
      dispatch(updatePendingCreativesCount(undefined as unknown as number));
    }

    return () => {
      if (cancelFunctions.FETCH_PENDING_CREATIVE_COUNT) {
        cancelFunctions.FETCH_PENDING_CREATIVE_COUNT();
      }
    };
  }, [environmentId]);
};
